/* Brief 3.5 PR 7-A — mechanical file split. Lifted verbatim from
   AdminMerchantDetail.jsx so the lint gate (PR 7-B) runs on small,
   focused files. No behaviour change. */
import { useEffect, useState } from "react";
import { AlertTriangle, Building2, CheckCircle2, Loader2, Lock, Pencil, Unlock, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { CollapsibleSection } from "./primitives";

// ---------- D. Form data ----------

// Canonical step labels (Feb 2026 schema). The pre-canonicalization
// labels ("Step 2 — Operations", "Step 3 — Contacts") were the legacy
// layout before validators.STEP_SCHEMAS moved company → step1, people
// → step2, business model → step3.
const STEP_LABELS = {
  1: "Step 1 — Company",
  2: "Step 2 — Directors / UBOs",
  3: "Step 3 — Business model",
};

// Hidden from the panel — primary_contact_phone is revealed via a
// separate audit-gated endpoint, not the inline edit affordance.
const HIDDEN_FIELD_KEYS = new Set(["primary_contact_phone"]);

// Sensitive keys still EDITABLE inline, but the audit row redacts the
// value. Mirrors backend `SENSITIVE_PATHS` in admin_merchant_form_edit.py.
const SENSITIVE_FIELD_KEYS = new Set(["primary_contact_phone"]);

// Best-effort enum hints surfaced when the user starts editing common
// fields where we know the allowed list. Optional — backend always
// returns the full enum on rejection so the input can recover.
const STATIC_ENUM_HINTS = {
  // populated at runtime as the user encounters rejections
};

export function FormDataPanel({ data, merchantId, identity }) {
  const editable = Boolean(identity?.is_admin_editable_state);
  const forceEditable = Boolean(identity?.is_admin_editable_state_with_force);
  const [forceMode, setForceMode] = useState(false);
  const [forceModalOpen, setForceModalOpen] = useState(false);
  const [enumHints, setEnumHints] = useState(STATIC_ENUM_HINTS);

  // For approved/live merchants, force-editing is gated behind a
  // confirmation modal.
  const effectiveEditable = editable || (forceEditable && forceMode);
  const status = identity?.status;

  const handleForceToggle = () => {
    if (forceMode) {
      setForceMode(false);
    } else {
      setForceModalOpen(true);
    }
  };

  const applyEnumHint = (path, expected) => {
    if (!expected || !expected.enum) return;
    setEnumHints((h) => ({ ...h, [path]: expected.enum }));
  };

  return (
    <CollapsibleSection icon={Building2} title="Form data" testId="merchant-section-form-data" defaultOpen={false}>
      <div className="mt-4 mb-5 flex items-center gap-3">
        {!editable && !forceEditable && (
          <span
            data-testid="form-data-readonly-banner"
            className="text-[11px] text-zinc-500 italic"
          >
            Read-only — merchant status {status ? `"${status}"` : "unknown"} does not allow inline edit.
          </span>
        )}
        {editable && !forceMode && (
          <span
            data-testid="form-data-editable-banner"
            className="inline-flex items-center gap-1.5 text-[11px] text-emerald-300"
          >
            <Pencil className="h-3 w-3" />
            Click any field to edit. Screening-relevant changes auto-re-fire.
          </span>
        )}
        {forceEditable && !editable && (
          <button
            type="button"
            data-testid="form-data-force-edit-toggle"
            onClick={handleForceToggle}
            className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[11px] transition-colors ${
              forceMode
                ? "border-amber-400/40 bg-amber-400/[0.10] text-amber-100"
                : "border-white/[0.10] bg-white/[0.02] text-zinc-400 hover:bg-white/[0.06]"
            }`}
          >
            {forceMode ? <Unlock className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
            {forceMode ? "Force edit ON" : "Force edit (approved/live)"}
          </button>
        )}
      </div>

      {[1, 2, 3].map((step) => (
        <FormStep
          key={step}
          step={step}
          fields={data[`step${step}`] || {}}
          editable={effectiveEditable}
          merchantId={merchantId}
          force={forceMode}
          enumHints={enumHints}
          onEnumLearned={applyEnumHint}
        />
      ))}

      {forceModalOpen && (
        <ForceEditConfirmModal
          status={status}
          onConfirm={() => { setForceMode(true); setForceModalOpen(false); }}
          onCancel={() => setForceModalOpen(false)}
        />
      )}
    </CollapsibleSection>
  );
}

function ForceEditConfirmModal({ status, onConfirm, onCancel }) {
  return (
    <div
      data-testid="force-edit-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
    >
      <div className="max-w-md rounded-2xl border border-amber-400/30 bg-zinc-950 p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-300 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-[14px] font-light text-white mb-2">
              Edit {status} merchant data?
            </h3>
            <p className="text-[12.5px] text-zinc-400 leading-relaxed mb-5">
              This merchant is already {status === "live" ? "processing payments" : "approved"}.
              Editing KYB data after that point is unusual and requires extra friction.
              Every edit is audit-logged with a force flag.
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="force-edit-cancel"
                onClick={onCancel}
                className="rounded-lg border border-white/[0.10] bg-white/[0.02] px-3 py-1.5 text-[12px] text-zinc-300 hover:bg-white/[0.06]"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="force-edit-confirm"
                onClick={onConfirm}
                className="rounded-lg border border-amber-400/40 bg-amber-400/[0.10] px-3 py-1.5 text-[12px] text-amber-100 hover:bg-amber-400/[0.16]"
              >
                I understand — enable force edit
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FormStep({ step, fields, editable, merchantId, force, enumHints, onEnumLearned }) {
  const entries = Object.entries(fields).filter(([k]) => !HIDDEN_FIELD_KEYS.has(k));
  if (entries.length === 0) return null;
  return (
    <div className="mt-5" data-testid={`form-step-${step}`}>
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500">— {STEP_LABELS[step] || `Step ${step}`}</div>
      <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
        {entries.map(([k, v]) => {
          const path = `step${step}.${k}`;
          // people_list arrays — render each person as an editable
          // sub-grid (one row per scalar field per person).
          if (Array.isArray(v) && v.length > 0 && typeof v[0] === "object") {
            return (
              <div key={k} className="sm:col-span-2">
                <dt className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-2">{k.replaceAll("_", " ")}</dt>
                <div className="space-y-3">
                  {v.map((person, idx) => (
                    <PersonCard
                      key={idx}
                      step={step}
                      arrayKey={k}
                      idx={idx}
                      person={person}
                      editable={editable}
                      merchantId={merchantId}
                      force={force}
                      enumHints={enumHints}
                      onEnumLearned={onEnumLearned}
                    />
                  ))}
                </div>
              </div>
            );
          }
          // Empty array — render the section label so admin sees an empty UBO list etc.
          if (Array.isArray(v) && v.length === 0) {
            return (
              <div key={k} className="sm:col-span-2">
                <dt className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">{k.replaceAll("_", " ")}</dt>
                <dd className="mt-1 text-[12px] text-zinc-600 italic">No entries</dd>
              </div>
            );
          }
          // Nested dict — render as read-only JSON for now (rare;
          // schema typically uses people_list for repeating structures).
          if (typeof v === "object" && v !== null) {
            return (
              <div key={k}>
                <dt className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">{k.replaceAll("_", " ")}</dt>
                <dd className="mt-1">
                  <pre className="text-[11.5px] text-zinc-300 whitespace-pre-wrap">{JSON.stringify(v, null, 2)}</pre>
                </dd>
              </div>
            );
          }
          // Scalar — editable inline.
          return (
            <div key={k}>
              <dt className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500">{k.replaceAll("_", " ")}</dt>
              <EditableField
                path={path}
                value={v}
                editable={editable}
                merchantId={merchantId}
                force={force}
                enumHint={enumHints[path]}
                onEnumLearned={onEnumLearned}
                sensitive={SENSITIVE_FIELD_KEYS.has(k)}
              />
            </div>
          );
        })}
      </dl>
    </div>
  );
}

function PersonCard({ step, arrayKey, idx, person, editable, merchantId, force, enumHints, onEnumLearned }) {
  return (
    <div
      data-testid={`person-card-${step}-${arrayKey}-${idx}`}
      className="rounded-lg border border-white/[0.06] bg-white/[0.01] p-3"
    >
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-600 mb-2">#{idx + 1}</div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
        {Object.entries(person).map(([fk, fv]) => {
          const path = `step${step}.${arrayKey}[${idx}].${fk}`;
          return (
            <div key={fk}>
              <dt className="text-[10px] uppercase tracking-[0.16em] text-zinc-600">{fk.replaceAll("_", " ")}</dt>
              <EditableField
                path={path}
                value={fv}
                editable={editable}
                merchantId={merchantId}
                force={force}
                enumHint={enumHints[path]}
                onEnumLearned={onEnumLearned}
              />
            </div>
          );
        })}
      </dl>
    </div>
  );
}

function EditableField({ path, value, editable, merchantId, force, enumHint, onEnumLearned, sensitive }) {
  const [editing, setEditing] = useState(false);
  // Local committed value — survives parent re-renders without a refetch.
  // We keep an optimistic copy so the panel doesn't lose section state
  // (collapsed/expanded) when refresh would re-fetch unified data.
  const [committed, setCommitted] = useState(value);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [justSaved, setJustSaved] = useState(false);

  // Sync committed + draft when the parent's `value` prop changes
  // (e.g., after refresh()). Real React Compiler false-positive — this
  // IS the standard prop-to-state sync pattern. Both writes target
  // local state derived from a prop, not a value computed inside the
  // effect. No infinite loop because `value` reference stability is
  // governed by the parent's data fetch, not this effect.
  /* eslint-disable */
  useEffect(() => { setCommitted(value); setDraft(value ?? ""); }, [value]);
  /* eslint-enable */

  const testId = `editable-field-${path.replaceAll(".", "-").replaceAll("[", "-").replaceAll("]", "")}`;
  const displayValue =
    committed === null || committed === undefined || committed === "" ? "—" : String(committed);

  const beginEdit = () => {
    if (!editable) return;
    setDraft(committed ?? "");
    setError("");
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setError("");
    setDraft(committed ?? "");
  };

  const commit = async () => {
    // Skip if unchanged.
    const newVal = draft === "" ? null : draft;
    if (newVal === committed) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const { data } = await api.patch(`/admin/merchants/${merchantId}/form-data`, {
        edits: [{ path, value: newVal }],
        force,
      });
      if (data.rejected && data.rejected.length > 0) {
        const rej = data.rejected[0];
        setError(rej.reason || "Rejected");
        if (rej.expected) {
          onEnumLearned?.(path, rej.expected);
        }
        setSaving(false);
        return;
      }
      if (data.screening_fired) {
        toast.success(`Saved. Screening re-fired (${data.screening_status || "ok"}).`);
      } else {
        toast.success("Saved.");
      }
      // Optimistic — update local committed value without a parent refetch
      // (a refetch would set loading=true and collapse the section).
      // Parent is notified via onSaved for cross-section invalidations
      // (cross-check counts, screening summary, etc.) but it's debounced.
      setCommitted(newVal);
      setEditing(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1500);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : detail?.message || "Save failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter") { e.preventDefault(); commit(); }
    else if (e.key === "Escape") { e.preventDefault(); cancel(); }
  };

  if (!editing) {
    return (
      <dd
        className={`mt-1 text-[13px] text-white break-words ${
          editable ? "cursor-pointer group hover:text-cyan-200" : ""
        } ${justSaved ? "text-emerald-300" : ""}`}
        onClick={beginEdit}
        data-testid={testId}
      >
        {sensitive && value ? "••••••" : displayValue}
        {editable && (
          <Pencil className="inline ml-1.5 h-3 w-3 opacity-0 group-hover:opacity-60 transition-opacity" />
        )}
        {justSaved && <CheckCircle2 className="inline ml-1.5 h-3 w-3 text-emerald-400" />}
      </dd>
    );
  }

  // Editing — render enum dropdown if hint available, else text input.
  return (
    <dd className="mt-1 space-y-1">
      <div className="flex items-center gap-2">
        {enumHint ? (
          <select
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            disabled={saving}
            data-testid={`${testId}-input`}
            className="flex-1 rounded border border-cyan-400/30 bg-white/[0.04] px-2 py-1 text-[13px] text-white focus:outline-none focus:border-cyan-400/60"
          >
            <option value="">— select —</option>
            {enumHint.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        ) : (
          <input
            autoFocus
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            onBlur={commit}
            disabled={saving}
            data-testid={`${testId}-input`}
            className="flex-1 rounded border border-cyan-400/30 bg-white/[0.04] px-2 py-1 text-[13px] text-white font-mono focus:outline-none focus:border-cyan-400/60"
          />
        )}
        <button
          type="button"
          onClick={commit}
          disabled={saving}
          data-testid={`${testId}-save`}
          className="rounded border border-emerald-400/30 bg-emerald-400/[0.06] px-2 py-1 text-emerald-200 hover:bg-emerald-400/[0.12] disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
        </button>
        <button
          type="button"
          onClick={cancel}
          disabled={saving}
          data-testid={`${testId}-cancel`}
          className="rounded border border-white/[0.10] bg-white/[0.02] px-2 py-1 text-zinc-400 hover:bg-white/[0.06] disabled:opacity-50"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {error && (
        <div
          data-testid={`${testId}-error`}
          className="text-[11px] text-rose-300"
        >
          {error}
        </div>
      )}
    </dd>
  );
}
